import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Papa from 'papaparse';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import { lightColors, themeBackgrounds } from '../../theme/theme';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';

interface Agent {
  _id?: string;
  id: number;
  name: string;
  applicationId: string;
  applicationName: string;
  avatarColor: string;
  agent_id?: string;
  organization_id?: string;
  agent_status?: string;
  created_at?: string;
  role?: string;
  max_concurrent_chats?: number;
}

interface ApiAgent {
  _id: string;
  organization_id: string;
  app_id: string;
  app_name: string;
  agent_id: string;
  agent_name: string;
  role?: string;
  max_concurrent_chats?: number;
  created_at: string;
  updated_at?: string;
  agent_status?: string;
  __v: number;
}

interface InvalidAgent {
  agent_id: string;
  agent_name: string;
  error: string;
  rowIndex: number;
  isFixed?: boolean;
}

interface UploadAgentItem {
  agent_id: string;
  agent_name: string;
  reason?: string;
}

interface UploadAgentsDecrypted {
  addedAgents?: UploadAgentItem[];
  alreadyExistingAgents?: UploadAgentItem[];
}

export default function ApplicationAgents(): React.ReactElement {
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const appId = useSelector((state: any) => state.applicationDetails.app_id);
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [agentName, setAgentName] = useState<string>('');
  const [agentId, setAgentId] = useState<string>('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingAgents, setIsLoadingAgents] = useState<boolean>(true);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState<boolean>(false);
  const [bulkStatusUpdateOpen, setBulkStatusUpdateOpen] = useState<boolean>(false);
  const [statusToUpdate, setStatusToUpdate] = useState<'Active' | 'InActive'>('Active');
  const [isUploadingCSV, setIsUploadingCSV] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState<boolean>(false);
  const [invalidAgents, setInvalidAgents] = useState<InvalidAgent[]>([]);
  const [validAgents, setValidAgents] = useState<Array<{ agent_id: string; agent_name: string }>>([]);

  useEffect(() => {
    if (!userDetails || !appId) return;
    fetchApplicationAgents();
  }, [userDetails, appId, page, rowsPerPage]);

  const fetchApplicationAgents = async (): Promise<void> => {
    if (!userDetails?.organization_id) return;

    setIsLoadingAgents(true);
    try {
      const payload = {
        request_id: `get_agents_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "get_application_agents",
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
          app_id: appId || '',
          page: page + 1,
          limit: rowsPerPage
        }, REQ_ENC_KEY)
      };

      const response = await httpService.post<any>(
        `${API_BASE_URL}/user/getApplicationAgentsForAdmin`,
        payload
      );

      if (response.data.status === 200 && response.data.data) {
        const decrypted = await decryptResponse<{
          agents?: ApiAgent[];
          pagination?: {
            page: number;
            limit: number;
            totalRecords: number;
            totalPages: number;
            hasNextPage: boolean;
            hasPreviousPage: boolean;
          };
        }>(response.data.data, getSessionKey() as string);

        const pageAgents = Array.isArray(decrypted)
          ? decrypted
          : (decrypted.agents ?? []);
        const pagination = Array.isArray(decrypted) ? undefined : decrypted.pagination;
        const transformedAgents: Agent[] = pageAgents.map((apiAgent: ApiAgent, index: number) => ({
          _id: apiAgent._id,
          id: page * rowsPerPage + index + 1,
          name: apiAgent.agent_name,
          applicationId: apiAgent.app_id,
          applicationName: apiAgent.app_name,
          avatarColor: lightColors[Math.floor(Math.random() * lightColors.length)],
          agent_id: apiAgent.agent_id,
          organization_id: apiAgent.organization_id,
          agent_status: apiAgent.agent_status || 'Active',
          created_at: apiAgent.created_at,
          role: apiAgent.role,
          max_concurrent_chats: apiAgent.max_concurrent_chats,
        }));

        setAgents(transformedAgents);
        setTotalRecords(pagination?.totalRecords ?? transformedAgents.length);
      } else {
        setAgents([]);
        setTotalRecords(0);
      }
      setSelectedAgents([]);
    } catch (error) {
      console.log('Error fetching application agents:', error);
      setAgents([]);
      setTotalRecords(0);
    } finally {
      setIsLoadingAgents(false);
    }
  };

  const processUploadAgentsResult = async (decryptedData: UploadAgentsDecrypted): Promise<void> => {
    const addedAgents = decryptedData?.addedAgents ?? [];
    const alreadyExistingAgents = decryptedData?.alreadyExistingAgents ?? [];
    const addedCount = addedAgents.length;
    const existingCount = alreadyExistingAgents.length;

    if (addedCount > 0) {
      toast.success(`${addedCount} agent(s) uploaded successfully!`, {
        position: 'top-right',
        autoClose: 3000
      });
    }

    if (existingCount > 0) {
      toast.warning(`${existingCount} agent(s) already exist`, {
        position: 'top-right',
        autoClose: 3000
      });
      downloadFailedAgentsCSV(alreadyExistingAgents);
    }

    await fetchApplicationAgents();
  };

  const handleAddAgentClick = (): void => setOpenAddDialog(true);

  const handleDialogClose = (): void => {
    setAgentName('');
    setAgentId('');
    setOpenAddDialog(false);
  };

  const handleSelectAgent = (selectedAgentId: string): void => {
    setSelectedAgents(prev =>
      prev.includes(selectedAgentId)
        ? prev.filter(id => id !== selectedAgentId)
        : [...prev, selectedAgentId]
    );
  };

  const handleSelectAll = (): void => {
    if (selectedAgents.length === filteredAgents.length) {
      setSelectedAgents([]);
    } else {
      const allIds = filteredAgents.map(agent => agent.agent_id || agent._id || '').filter(Boolean);
      setSelectedAgents(allIds);
    }
  };

  const handleBulkDelete = (): void => {
    setBulkDeleteOpen(true);
  };

  const confirmBulkDelete = async (): Promise<void> => {
    if (selectedAgents.length === 0) return;

    setIsLoading(true);
    try {
      const bulkDeletePayload = {
        request_id: `bulk_delete_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "remove_multiple_application_participants",
        data: {
          organization_id: userDetails?.organization_id || '',
          app_id: appId || '',
          participant_ids: selectedAgents
        }
      };

      type BulkDeleteResponse = {
        status: number;
        message?: string;
        data?: {
          removed_count: number;
          requested_count: number;
          not_found_count: number;
          participant_ids: string[];
        };
        error?: any;
        [key: string]: any;
      };

      const response = await httpService.post<BulkDeleteResponse>(
        `${API_BASE_URL}/user/removeApplicationParticipant`,
        bulkDeletePayload
      );

      console.log("Bulk Delete Response:", response);

      if (response.data.status === 200) {
        const { removed_count, not_found_count } = response.data.data || {};

        if (removed_count && removed_count > 0) {
          toast.success(`${removed_count} agent(s) removed successfully!`, {
            position: 'top-right',
            autoClose: 3000
          });
        }

        if (not_found_count && not_found_count > 0) {
          toast.warning(`${not_found_count} agent(s) were not found`, {
            position: 'top-right',
            autoClose: 3000
          });
        }

        await fetchApplicationAgents();
        setSelectedAgents([]);
      } else {
        toast.error(response.data.message || 'Failed to remove agents. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred while removing agents. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsLoading(false);
      setBulkDeleteOpen(false);
    }
  };

  const cancelBulkDelete = (): void => {
    setBulkDeleteOpen(false);
  };

  const handleBulkStatusUpdate = (status: 'Active' | 'InActive'): void => {
    setStatusToUpdate(status);
    setBulkStatusUpdateOpen(true);
  };

  const confirmBulkStatusUpdate = async (): Promise<void> => {
    if (selectedAgents.length === 0) return;

    setIsLoading(true);
    try {
      const bulkStatusUpdatePayload = {
        request_id: `bulk_status_update_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "update_multiple_agent_status",
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_id: appId || '',
          agents_ids: selectedAgents,
          agent_status: statusToUpdate
        }, REQ_ENC_KEY)
      };

      type BulkStatusUpdateResponse = {
        status: number;
        message?: string;
        data?: any;
        error?: any;
        [key: string]: any;
      };

      const response = await httpService.post<BulkStatusUpdateResponse>(
        `${API_BASE_URL}/user/updateAgentsStatus`,
        bulkStatusUpdatePayload
      );

      if (response.data.status === 200 && response.data.data) {
        const decryptedData = await decryptResponse<any>(response.data.data, getSessionKey() as string);
        const { updated_count, not_found_count } = decryptedData || {};

        if (updated_count && updated_count > 0) {
          toast.success(`${updated_count} agent(s) status updated to ${statusToUpdate} successfully!`, {
            position: 'top-right',
            autoClose: 3000
          });
        } else {
          toast.success("Request submitted successfully!", {
            position: 'top-right',
            autoClose: 3000
          });
        }

        if (not_found_count && not_found_count > 0) {
          toast.warning(`${not_found_count} agent(s) were not found`, {
            position: 'top-right',
            autoClose: 3000
          });
        }

        await fetchApplicationAgents();
        setSelectedAgents([]);
      } else {
        toast.error(response.data.message || 'Failed to update agent status. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred while updating agent status. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsLoading(false);
      setBulkStatusUpdateOpen(false);
    }
  };

  const cancelBulkStatusUpdate = (): void => {
    setBulkStatusUpdateOpen(false);
  };

  const handleCSVUploadClick = (): void => {
    fileInputRef.current?.click();
  };

  const validateAgentName = (name: string): { isValid: boolean; error: string } => {
    const hasNumbers = /\d/.test(name);
    if (hasNumbers) {
      return { isValid: false, error: 'Name contains numbers' };
    }
    if (!name.trim() || !/[a-zA-Z]/.test(name)) {
      return { isValid: false, error: 'Name must contain letters' };
    }
    return { isValid: true, error: '' };
  };

  const handleCSVFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a valid CSV file', {
        position: 'top-right',
        autoClose: 3000
      });
      return;
    }

    setIsUploadingCSV(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          if (!results.data || results.data.length === 0) {
            toast.error('CSV file is empty', {
              position: 'top-right',
              autoClose: 3000
            });
            setIsUploadingCSV(false);
            return;
          }

          const allAgents = results.data.map((row: any, index: number) => ({
            agent_id: row.agent_id || row.Agent_ID || row['Agent ID'] || '',
            agent_name: row.agent_name || row.Agent_Name || row['Agent Name'] || row.name || row.Name || '',
            rowIndex: index
          })).filter(agent => agent.agent_id && agent.agent_name);

          if (allAgents.length === 0) {
            toast.error('No valid agents found in CSV. Please ensure columns are named "agent_id" and "agent_name"', {
              position: 'top-right',
              autoClose: 5000
            });
            setIsUploadingCSV(false);
            return;
          }

          const validList: Array<{ agent_id: string; agent_name: string }> = [];
          const invalidList: InvalidAgent[] = [];

          allAgents.forEach((agent) => {
            const validation = validateAgentName(agent.agent_name);
            if (validation.isValid) {
              validList.push({
                agent_id: agent.agent_id,
                agent_name: agent.agent_name
              });
            } else {
              invalidList.push({
                agent_id: agent.agent_id,
                agent_name: agent.agent_name,
                error: validation.error,
                rowIndex: agent.rowIndex
              });
            }
          });

          if (invalidList.length > 0) {
            setInvalidAgents(invalidList);
            setValidAgents(validList);
            setValidationDialogOpen(true);
            setIsUploadingCSV(false);
            return;
          }

          const uploadAgents = validList;

          const uploadPayload = {
            data: await encryptResponse({
              organization_id: userDetails?.organization_id || '',
              app_id: appId || '',
              agents: uploadAgents.map(agent => ({
                agent_id: agent.agent_id,
                agent_name: agent.agent_name
              }))
            }, REQ_ENC_KEY)
          };

          type UploadAgentsResponse = {
            status: number;
            message?: string;
            data?: any;
            error?: any;
          };

          const response = await httpService.post<UploadAgentsResponse>(
            `${API_BASE_URL}/user/uploadApplicationAgents`,
            uploadPayload
          );

          if (response.data.status === 200 && response.data.data) {
            const decryptedData = await decryptResponse<UploadAgentsDecrypted>(
              response.data.data,
              getSessionKey() as string
            );
            await processUploadAgentsResult(decryptedData);
          } else {
            toast.error(response.data.message || 'Failed to upload agents. Please try again.', {
              position: 'top-right',
              autoClose: 3000
            });
          }
        } catch (error) {
          console.error('Error uploading agents:', error);
          toast.error('An error occurred while uploading agents. Please try again.', {
            position: 'top-right',
            autoClose: 3000
          });
        } finally {
          setIsUploadingCSV(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        toast.error('Failed to parse CSV file. Please check the file format.', {
          position: 'top-right',
          autoClose: 3000
        });
        setIsUploadingCSV(false);
      }
    });
  };

  const handleInvalidAgentChange = (index: number, field: 'agent_id' | 'agent_name', value: string): void => {
    const updated = [...invalidAgents];
    updated[index][field] = value;

    if (field === 'agent_name') {
      const validation = validateAgentName(value);
      if (validation.isValid && updated[index].agent_id.trim()) {
        updated[index].isFixed = true;
        updated[index].error = '';

        const validAgent = {
          agent_id: updated[index].agent_id,
          agent_name: value
        };

        setValidAgents(prev => {
          const exists = prev.some(agent => agent.agent_id === validAgent.agent_id);
          if (!exists) {
            return [...prev, validAgent];
          }
          return prev;
        });
      } else {
        updated[index].error = validation.error;
        updated[index].isFixed = false;
        setValidAgents(prev =>
          prev.filter(agent => agent.agent_id !== updated[index].agent_id)
        );
      }
    }

    setInvalidAgents(updated);
  };

  const handleRemoveInvalidAgent = (index: number): void => {
    const updated = invalidAgents.filter((_, i) => i !== index);
    setInvalidAgents(updated);
  };

  const handleValidationDialogClose = (): void => {
    setValidationDialogOpen(false);
    setInvalidAgents([]);
    setValidAgents([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadAfterValidation = async (): Promise<void> => {
    const stillInvalid: InvalidAgent[] = [];
    const nowValid: Array<{ agent_id: string; agent_name: string }> = [];

    invalidAgents.forEach((agent) => {
      const validation = validateAgentName(agent.agent_name);
      if (validation.isValid && agent.agent_id.trim()) {
        nowValid.push({
          agent_id: agent.agent_id,
          agent_name: agent.agent_name
        });
      } else {
        stillInvalid.push({
          ...agent,
          error: validation.error || 'Invalid agent ID'
        });
      }
    });

    if (stillInvalid.length > 0) {
      setInvalidAgents(stillInvalid);
      toast.error(`${stillInvalid.length} agent(s) still have validation errors. Please fix them before uploading.`, {
        position: 'top-right',
        autoClose: 3000
      });
      return;
    }

    const allValidAgents = [...validAgents, ...nowValid];
    const uniqueAgents = allValidAgents.reduce((acc, current) => {
      const exists = acc.find(item => item.agent_id === current.agent_id);
      if (!exists) {
        acc.push(current);
      }
      return acc;
    }, [] as Array<{ agent_id: string; agent_name: string }>);

    if (uniqueAgents.length === 0) {
      toast.error('No valid agents to upload', {
        position: 'top-right',
        autoClose: 3000
      });
      return;
    }

    setValidationDialogOpen(false);
    setIsUploadingCSV(true);

    try {
      const uploadPayload = {
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_id: appId || '',
          agents: uniqueAgents.map(agent => ({
            agent_id: agent.agent_id,
            agent_name: agent.agent_name
          }))
        }, REQ_ENC_KEY)
      };

      type UploadAgentsResponse = {
        status: number;
        message?: string;
        data?: any;
        error?: any;
      };

      const response = await httpService.post<UploadAgentsResponse>(
        `${API_BASE_URL}/user/uploadApplicationAgents`,
        uploadPayload
      );

      if (response.data.status === 200 && response.data.data) {
        const decryptedData = await decryptResponse<UploadAgentsDecrypted>(
          response.data.data,
          getSessionKey() as string
        );
        await processUploadAgentsResult(decryptedData);
      } else {
        toast.error(response.data.message || 'Failed to upload agents. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error uploading agents:', error);
      toast.error('An error occurred while uploading agents. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsUploadingCSV(false);
      setInvalidAgents([]);
      setValidAgents([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadFailedAgentsCSV = (failedAgents: UploadAgentItem[]): void => {
    if (failedAgents.length === 0) return;

    const csvContent = Papa.unparse(failedAgents, {
      header: true,
      columns: ['agent_id', 'agent_name', 'reason'],
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `failed_agents_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.info('Failed agents downloaded as CSV', {
      position: 'top-right',
      autoClose: 3000
    });
  };

  const handleAddAgent = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const addAgentPayload = {
        request_id: `add_agent_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "add_application_agent",
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_id: appId || '',
          agent_id: agentId,
          agent_name: agentName
        }, REQ_ENC_KEY)
      };

      type AddAgentResponse = {
        status: number;
        data?: any;
        message?: string;
        error?: any;
        [key: string]: any
      };

      const response = await httpService.post<AddAgentResponse>(
        `${API_BASE_URL}/user/addApplicationAgent`,
        addAgentPayload
      );

      if (response.data.status === 200 && response.data.data) {
        await fetchApplicationAgents();
        handleDialogClose();
        toast.success('Agent added successfully!', {
          position: 'top-right',
          autoClose: 3000
        });
      } else {
        toast.error(response.data.message || 'Failed to add agent. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred while adding the agent. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number): void => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredAgents: Agent[] = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (agent.agent_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.applicationName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedAgentRecords = agents.filter((agent) => {
    const id = agent.agent_id || agent._id || '';
    return selectedAgents.includes(id);
  });

  const isInactiveStatus = (status?: string): boolean =>
    status === 'InActive' || status === 'Inactive';

  const allSelectedAreInactive =
    selectedAgentRecords.length > 0 &&
    selectedAgentRecords.every((agent) => isInactiveStatus(agent.agent_status));

  const allSelectedAreActive =
    selectedAgentRecords.length > 0 &&
    selectedAgentRecords.every((agent) => agent.agent_status === 'Active');

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <PageHeader
          heading="Application Agents"
          subheading="Manage agents associated with your applications"
          variant="gradient"
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <TextField
          placeholder="Search agents, applications..."
          size="small"
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{ endAdornment: <IconButton><SearchIcon /></IconButton> }}
        />
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={handleAddAgentClick}
        >
          Add Agent
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={handleCSVUploadClick}
          disabled={isUploadingCSV}
          sx={{
            background: isUploadingCSV ? 'grey' : themeBackgrounds.default,
            color: '#ffffff',
            '&:hover': {
              background: themeBackgrounds.default,
              opacity: 0.9,
            },
          }}
        >
          {isUploadingCSV ? 'Uploading...' : 'Upload CSV'}
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleCSVFileChange}
        />
        {selectedAgents.length > 0 && (
          <>
            {allSelectedAreInactive && (
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => handleBulkStatusUpdate('Active')}
                sx={{ mr: 1 }}
              >
                Activate Selected ({selectedAgents.length})
              </Button>
            )}
            {allSelectedAreActive && (
              <Button
                variant="contained"
                color="warning"
                size="small"
                onClick={() => handleBulkStatusUpdate('InActive')}
                sx={{ mr: 1 }}
              >
                Deactivate Selected ({selectedAgents.length})
              </Button>
            )}
            {/* <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
            >
              Delete Selected ({selectedAgents.length})
            </Button> */}
          </>
        )}
      </Box>

      <Paper>
        {isLoadingAgents ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading agents...</Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedAgents.length > 0 && selectedAgents.length < filteredAgents.length}
                      checked={filteredAgents.length > 0 && selectedAgents.length === filteredAgents.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Agent ID</TableCell>
                  <TableCell>Application</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Max Concurrent</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAgents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No agents found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAgents.map((agent) => {
                    const rowAgentId = agent.agent_id || agent._id || '';
                    const isSelected = selectedAgents.includes(rowAgentId);

                    return (
                      <TableRow key={agent._id || agent.id} selected={isSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectAgent(rowAgentId)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ bgcolor: agent.avatarColor }}>
                              {agent.name.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" fontWeight="medium">
                              {agent.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {agent.agent_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {agent.applicationName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {agent.role || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {agent.max_concurrent_chats ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              bgcolor:
                                agent.agent_status === 'Active' ? '#e8f5e8' :
                                  agent.agent_status === 'Inactive' || agent.agent_status === 'InActive' ? '#fff3cd' :
                                    agent.agent_status === 'Pending' ? '#e3f2fd' :
                                      agent.agent_status === 'Blocked' ? '#ffebee' :
                                        '#f5f5f5',
                              color:
                                agent.agent_status === 'Active' ? '#2e7d32' :
                                  agent.agent_status === 'Inactive' || agent.agent_status === 'InActive' ? '#856404' :
                                    agent.agent_status === 'Pending' ? '#1565c0' :
                                      agent.agent_status === 'Blocked' ? '#c62828' :
                                        '#666666'
                            }}
                          >
                            {agent.agent_status || 'Active'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {agent.created_at ? new Date(agent.created_at).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
      <Dialog open={openAddDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add Application Agent
          <IconButton onClick={handleDialogClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add a new agent to an application.
          </Typography>

          <TextField
            fullWidth
            label="Agent Name"
            placeholder="Enter agent name"
            value={agentName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAgentName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Agent ID"
            placeholder="Enter agent ID"
            value={agentId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAgentId(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!agentName || !agentId || isLoading}
            onClick={handleAddAgent}
            sx={{
              background: (!agentName || !agentId || isLoading) ? 'grey' : themeBackgrounds.default,
              color: '#ffffff',
              '&:hover': {
                background: themeBackgrounds.default,
                opacity: 0.9,
              },
            }}
          >
            {isLoading ? 'Adding...' : 'Add Agent'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={bulkStatusUpdateOpen} onClose={cancelBulkStatusUpdate} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Status Update</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to update the status of <strong>{selectedAgents.length}</strong> selected agent(s) to <strong>{statusToUpdate}</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This will change the agent status in the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelBulkStatusUpdate} color="inherit" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={confirmBulkStatusUpdate}
            color={statusToUpdate === 'Active' ? 'success' : 'warning'}
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Updating...' : `Update to ${statusToUpdate}`}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={bulkDeleteOpen} onClose={cancelBulkDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{selectedAgents.length}</strong> selected agent(s)?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelBulkDelete} color="inherit" disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={confirmBulkDelete} color="error" variant="contained" disabled={isLoading}>
            {isLoading ? 'Deleting...' : 'Delete Selected'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={validationDialogOpen}
        onClose={handleValidationDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Validation Errors Found
          <IconButton
            onClick={handleValidationDialogClose}
            sx={{ ml: 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {invalidAgents.filter(agent => !agent.isFixed).length} agent(s) have validation errors. Please correct the errors below before uploading.
            {validAgents.length > 0 && (
              <Box component="span" sx={{ color: 'success.main', fontWeight: 'bold', ml: 1 }}>
                ({validAgents.length} valid agent(s) will be uploaded)
              </Box>
            )}
          </Typography>

          <Paper variant="outlined" sx={{ maxHeight: '400px', overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Agent ID</TableCell>
                  <TableCell>Agent Name</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invalidAgents.map((agent, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor: agent.isFixed ? '#e8f5e9' : 'transparent',
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {agent.rowIndex + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={agent.agent_id}
                        onChange={(e) => handleInvalidAgentChange(index, 'agent_id', e.target.value)}
                        placeholder="Enter ID"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={agent.agent_name}
                        onChange={(e) => handleInvalidAgentChange(index, 'agent_name', e.target.value)}
                        error={!agent.isFixed && !!agent.error}
                        placeholder="Enter valid name"
                      />
                    </TableCell>
                    <TableCell>
                      {agent.isFixed ? (
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold' }}
                        >
                          <CheckCircleIcon sx={{ fontSize: 16 }} />
                          Valid
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <WarningIcon sx={{ fontSize: 16 }} />
                          {agent.error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveInvalidAgent(index)}
                        title="Remove this agent"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
              Validation Rules:
            </Typography>
            <Typography variant="caption" display="block">
              • Agent names must not contain numbers
            </Typography>
            <Typography variant="caption" display="block">
              • Names must contain at least one letter
            </Typography>
            <Typography variant="caption" display="block">
              • Both agent ID and name are required
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleValidationDialogClose}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadAfterValidation}
            variant="contained"
            disabled={isUploadingCSV}
            sx={{
              background: isUploadingCSV ? 'grey' : themeBackgrounds.default,
              color: '#ffffff',
              '&:hover': {
                background: themeBackgrounds.default,
                opacity: 0.9,
              },
            }}
          >
            {isUploadingCSV ? 'Uploading...' : 'Upload Valid Agents'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
